import { useEffect, useRef, useState } from 'react'
import { ApiError } from '@/lib/api'
import { describeError } from '@/lib/auth'
import { desktopLoginApi, desktopLoginURL, type DesktopLoginRequest } from '@/lib/desktopLogin'
import { useAuthStore } from '@/store/auth'
import styles from '@/pages/AuthPage.module.css'

/** Credentials live in memory only. Unmount/cancel stops polling and invalidates the request. */
export function DesktopBrowserLogin() {
  const [request, setRequest] = useState<DesktopLoginRequest | null>(null)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const generation = useRef(0)
  const active = useRef<DesktopLoginRequest | null>(null)
  const setSession = useAuthStore((s) => s.setSession)
  const cancel = () => {
    generation.current++
    const old = active.current
    active.current = null
    setRequest(null)
    setStarting(false)
    if (old) void desktopLoginApi.cancel(old).catch(() => undefined)
  }
  useEffect(() => () => {
    generation.current++
    const old = active.current
    active.current = null
    if (old) void desktopLoginApi.cancel(old).catch(() => undefined)
  }, [])
  const start = async () => {
    const current = ++generation.current
    setStarting(true)
    setError(null)
    try {
      const r = await desktopLoginApi.start()
      if (generation.current !== current) { void desktopLoginApi.cancel(r).catch(() => undefined); return }
      active.current = r
      setRequest(r)
      window.open(desktopLoginURL(r.id), '_blank', 'noopener,noreferrer')
    } catch (err) {
      if (generation.current === current) setError(describeError(err).message)
    } finally { if (generation.current === current) setStarting(false) }
  }
  useEffect(() => {
    if (!request) return
    const current = generation.current
    const controller = new AbortController()
    const deadline = Date.now() + request.expires_in * 1000
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      if (controller.signal.aborted || generation.current !== current) return
      if (Date.now() >= deadline) { setError('Запрос входа истёк. Попробуйте ещё раз.'); cancel(); return }
      let delay = Math.max(3, request.interval) * 1000
      try {
        const res = await desktopLoginApi.poll(request, controller.signal)
        if (controller.signal.aborted || generation.current !== current) return
        if (res.status === 'denied') { setError('Вход не подтверждён.'); cancel(); return }
        if (res.session) {
          if (useAuthStore.getState().status !== 'anonymous') { cancel(); return }
          active.current = null
          generation.current++
          setSession(res.session)
          return
        }
        setError(null)
      } catch (err) {
        if (controller.signal.aborted || generation.current !== current) return
        if (err instanceof ApiError && [400, 401, 403, 410].includes(err.status)) {
          setError(describeError(err).message); cancel(); return
        }
        setError('Нет связи с сервером. Повторяем проверку…')
        delay = Math.max(delay, err instanceof ApiError ? (err.retryAfter ?? 5) * 1000 : 5000)
      }
      timer = setTimeout(() => void poll(), delay)
    }
    timer = setTimeout(() => void poll(), request.interval * 1000)
    return () => { controller.abort(); clearTimeout(timer) }
  }, [request, setSession])
  return <div className={styles.browserLogin}>
    {request ? <>
      <p className={styles.muted} aria-live="polite">Ждём подтверждения входа…</p>
      <a className={styles.link} href={desktopLoginURL(request.id)} target="_blank" rel="noopener noreferrer">Открыть браузер ещё раз</a>
      <button type="button" className={styles.link} onClick={cancel}>Отменить</button>
    </> : <button type="button" className={styles.primary} disabled={starting} onClick={() => void start()}>{starting ? 'Открываем…' : 'Войти через браузер'}</button>}
    {error ? <p className={styles.error} role="alert">{error}</p> : null}
  </div>
}
